import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createRecord, updateRecord } from '../../services/recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { TabbedModal } from './TabbedModal';
import { usePageLayout } from '../../hooks/useObjectManager';
import { EditSection } from '../edit/EditSection';
import { useToast } from '../../contexts/ToastContext';
import { ListViewRecord } from '../../types/listView';
import { User, CheckSquare, Package, Mail, FileText, Paperclip, History, Link2 } from 'lucide-react';
import { validateRecord, type ValidationError } from '../../services/queries/validationRuleQueries';
import { ValidationErrors } from '../common/ValidationErrors';
import { RelatedRecordsTab } from '../tabs/RelatedRecordsTab';
import { ActivityTab } from '../tabs/ActivityTab';

interface ClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  client?: ListViewRecord | null; // If provided, edit mode. If null, create mode
}

export const ClientModal: React.FC<ClientModalProps> = ({
  isOpen,
  onClose,
  client
}) => {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();
  const isEditMode = !!client;

  // Fetch page layout (uses Record Pages assignment system)
  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Client__c', client);

  // Define tabs based on mode
  const tabs = isEditMode
    ? [
        // EDIT MODE: All tabs
        { id: 'overview', label: 'Overview', icon: User },
        { id: 'related', label: 'Related', icon: Link2 },
        { id: 'tasks', label: 'Tasks', icon: CheckSquare },
        { id: 'products', label: 'Products', icon: Package },
        { id: 'emails', label: 'Emails', icon: Mail },
        { id: 'notes', label: 'Notes', icon: FileText },
        { id: 'files', label: 'Files', icon: Paperclip },
        { id: 'history', label: 'History', icon: History }
      ]
    : [
        // CREATE MODE: Overview only
        { id: 'overview', label: 'Overview', icon: User }
      ];

  // Form state - populated from record properties directly
  const [formData, setFormData] = useState<Record<string, any>>({});

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validationRuleErrors, setValidationRuleErrors] = useState<ValidationError[]>([]);

  // Notes state
  const [notes, setNotes] = useState<string[]>([]);
  const [newNote, setNewNote] = useState('');

  const shouldGobodo = shouldUseGobodo();

  // Populate form from record properties — no hardcoded field list
  useEffect(() => {
    if (client) {
      // Spread all record properties into form state so EditSection can read any key
      const data: Record<string, any> = {};
      for (const [key, value] of Object.entries(client)) {
        if (value !== undefined && value !== null && typeof value !== 'object') {
          data[key] = value;
        }
      }
      // When Gobodo: ensure AccountRelField__c is set from accountId so layout field shows correctly
      if (shouldGobodo) {
        const accountVal = (client as any).AccountRelField__c ?? (client as any).accountId ?? (client as any).AccountId__c;
        if (accountVal) {
          data.AccountRelField__c = accountVal;
        }
      }
      setFormData(data);
      setNotes(client.notes as string[] || []);
    } else {
      setFormData({});
      setNotes([]);
    }
    setErrors({});
    setValidationRuleErrors([]);
    setNewNote('');
  }, [client, isOpen]);

  // Handle field changes (for dynamic fields)
  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Handle change events (legacy support)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    handleFieldChange(name, value);
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      setNotes([...notes, newNote.trim()]);
      setNewNote('');
    }
  };

  const handleRemoveNote = (index: number) => {
    setNotes(notes.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const name = String(formData.name || formData.Name__c || '').trim();
    const email = String(formData.email || formData.Email__c || '').trim();

    if (!name) {
      newErrors.name = 'Account name is required';
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, any> & { notes: string[] }) => {
      // Send form data as-is — field names match what Gobodo stored
      // recordStorageService / gobodoRecordService handle type conversion
      const { notes: _notes, ...fields } = data;
      const gobodoData: Record<string, any> = {};
      for (const [key, value] of Object.entries(fields)) {
        if (value !== undefined && value !== null && value !== '' && key !== 'id' && key !== 'label') {
          gobodoData[key] = value;
        }
      }
      // When Gobodo: use AccountRelField__c for account link, not AccountId__c
      if (shouldGobodo) {
        const accountVal = data.AccountRelField__c ?? data.AccountId__c ?? data.accountId;
        if (accountVal) {
          gobodoData.AccountRelField__c = accountVal;
        }
        delete gobodoData.AccountId__c;
      }

      if (isEditMode && client) {
        const updated = await updateRecord('Client__c', String(client.id), gobodoData);
        if (!updated) throw new Error('Failed to update client');
        return { ...client, ...data, id: updated.id };
      } else {
        const created = await createRecord('Client__c', gobodoData);
        return { ...data, id: created.id };
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clientListViews'] });
      queryClient.invalidateQueries({ queryKey: ['clientRecords'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['clientListViewData'] });
      queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Client__c'] });
      queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Client'] });
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['client', client?.id] });
      }

      const clientName = (data as any).Name__c ?? (data as any).name ?? 'Client';
      success(
        isEditMode ? 'Client updated' : 'Client created',
        isEditMode ? `${clientName} has been updated` : `${clientName} has been created`
      );
      onClose();
    },
    onError: (error: any) => {
      console.error('[ClientModal] Save error:', error);
      const msg = error?.response?.data?.message ?? error?.message ?? 'Failed to save client. Please try again.';
      showError('Error', typeof msg === 'string' ? msg : 'Failed to save client. Please try again.');
    }
  });

  const handleSubmit = (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    setValidationRuleErrors([]);

    if (!validate()) {
      showError('Validation Error', 'Please fix the errors before submitting');
      return;
    }

    // Build validation record from form data using field_ prefix convention
    const recordData: Record<string, any> = {
      field_name: formData.name || formData.Name__c || '',
      field_email: formData.email || formData.Email__c || '',
      field_phone: formData.phone || formData.Phone__c || '',
      field_status: formData.status || formData.Status__c || '',
    };

    const validationResult = validateRecord('Client__c', recordData);

    if (!validationResult.isValid) {
      setValidationRuleErrors(validationResult.errors);
      showError('Validation Rule Error', 'Record does not meet validation requirements');
      return;
    }
    saveMutation.mutate({ ...formData, notes });
  };

  return (
    <TabbedModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Client' : 'New Client'}
      tabs={tabs}
      maxWidth="2xl"
      actions={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#334155] rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending
              ? 'Saving...'
              : isEditMode ? 'Save Changes' : 'Create Client'
            }
          </button>
        </div>
      }
    >
      {(activeTab) => (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Validation Rule Errors */}
          {validationRuleErrors.length > 0 && (
            <ValidationErrors
              errors={validationRuleErrors}
              onDismiss={() => setValidationRuleErrors([])}
              className="mb-4"
            />
          )}

          {activeTab === 'overview' && (
            <div className="max-h-[60vh] overflow-y-auto pr-2">
              {loadingLayout ? (
                <div className="space-y-6">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i}>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
                        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : pageLayout?.sections && pageLayout.sections.length > 0 ? (
                pageLayout.sections.map(section => (
                  <EditSection
                    key={section.id}
                    section={section}
                    objectApiName="Client__c"
                    formData={formData}
                    onChange={handleFieldChange}
                    errors={errors}
                    disabled={saveMutation.isPending}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>No page layout configured for Client.</p>
                  <p className="text-sm mt-2">Go to Object Manager to configure page layouts.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'related' && client?.id && (
            <RelatedRecordsTab
              recordId={client.id}
              objectType="Client__c"
              recordName={client.name as string}
            />
          )}

          {activeTab === 'tasks' && (
            <div className="text-center py-12">
              <CheckSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Tasks
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Tasks related to this client will appear here.
              </p>
            </div>
          )}

          {activeTab === 'products' && (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Products
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Products and services for this client will appear here.
              </p>
            </div>
          )}

          {activeTab === 'emails' && (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Emails
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Email correspondence with this client will appear here.
              </p>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              {/* Add Note */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Add Note
                </label>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Type your note here..."
                  rows={4}
                  className="w-full px-3 py-2 bg-white dark:bg-[#0f172a] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleAddNote}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Note
                </button>
              </div>

              {/* Existing Notes */}
              {notes.length > 0 ? (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Notes ({notes.length})
                  </label>
                  <div className="space-y-2">
                    {notes.map((note, index) => (
                      <div
                        key={index}
                        className="p-3 bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-gray-700 rounded-lg"
                      >
                        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{note}</p>
                        <button
                          type="button"
                          onClick={() => handleRemoveNote(index)}
                          className="text-sm text-red-600 dark:text-red-400 hover:underline mt-2"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No notes yet. Add your first note above.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'files' && (
            <div className="text-center py-12">
              <Paperclip className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Files
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Documents and files attached to this client will appear here.
              </p>
            </div>
          )}

          {activeTab === 'history' && client?.id && (
            <ActivityTab
              recordId={client.id}
              objectType="Client__c"
              recordName={client.name as string}
            />
          )}
        </form>
      )}
    </TabbedModal>
  );
};
